import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/images/fit-dash-icon.png';
import { useAuth } from '../contexts/AuthContext';
import SignUpModal from './SignUpModal';
import '../styles/Header.scss';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  // Function to check if a path is active
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    // If the sign-up modal is open, close it when toggling the menu
    if (showSignUpModal) {
      setShowSignUpModal(false);
    }
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Track window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close mobile menu and sign-up modal when location changes (page navigation)
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowSignUpModal(false);
  }, [location.pathname]);

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <div className="logo">
            <Link to="/" className="logo-container">
              <img src={logo} alt="Fit Dash Icon" />
              <span className="logo-text">Fit Dash</span>
            </Link>
          </div>

          <div className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}>
            <span>Menu</span>
          </div>

          <nav className={`main-nav ${mobileMenuOpen ? 'active' : ''}`}>
            <ul>
              <li className={isActive('/') ? 'active' : ''}>
                <Link to="/" onClick={() => isMobile && setMobileMenuOpen(false)}>Home</Link>
              </li>
              <li className={isActive('/about') ? 'active' : ''}>
                <Link to="/about" onClick={() => isMobile && setMobileMenuOpen(false)}>About</Link>
              </li>
              <li className={isActive('/ebooks') ? 'active' : ''}>
                <Link to="/ebooks" onClick={() => isMobile && setMobileMenuOpen(false)}>eBooks</Link>
              </li>
              <li className={isActive('/videos') ? 'active' : ''}>
                <Link to="/videos" onClick={() => isMobile && setMobileMenuOpen(false)}>Videos</Link>
              </li>
              <li className={isActive('/plans') ? 'active' : ''}>
                <Link to="/plans" onClick={() => isMobile && setMobileMenuOpen(false)}>Plans</Link>
              </li>
              <li className={isActive('/faq') ? 'active' : ''}>
                <Link to="/faq" onClick={() => isMobile && setMobileMenuOpen(false)}>FAQ</Link>
              </li>
              <li className={isActive('/contact') ? 'active' : ''}>
                <Link to="/contact" onClick={() => isMobile && setMobileMenuOpen(false)}>Contact</Link>
              </li>
              {!isAuthenticated && isMobile && (
                <>
                  <li>
                    <button
                      className="cta-button"
                      onClick={() => {
                        console.log('Mobile Start Trial clicked');
                        setMobileMenuOpen(false);
                        navigate('/plans');
                      }}
                    >
                      Start Trial
                    </button>
                  </li>
                  <li>
                    <Link to="/login" className="login-link" onClick={() => setMobileMenuOpen(false)}>
                      Login
                    </Link>
                  </li>
                </>
              )}
              {isAuthenticated && isMobile && (
                <li>
                  <button className="sign-out-btn mobile-sign-out" onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                    if (location.pathname.startsWith('/learn')) {
                      navigate('/');
                    }
                  }}>Sign Out</button>
                </li>
              )}
            </ul>
          </nav>

          <div className={`auth-buttons ${mobileMenuOpen && isMobile ? 'active' : ''}`}>
            {isAuthenticated ? (
              <div className="user-section">
                <span className="user-greeting">Hello, {user?.name || 'User'}</span>
                {!isMobile && (
                  <button
                    className="sign-out-btn desktop-sign-out"
                    onClick={() => {
                      logout();
                      if (location.pathname.startsWith('/learn')) {
                        navigate('/');
                      }
                    }}
                  >
                    Sign Out
                  </button>
                )}
              </div>
            ) : !isMobile ? (
              <div className="desktop-auth-buttons">
                <button
                  className="sign-in-btn"
                  onClick={() => {
                    console.log('Desktop Start Trial clicked');
                    navigate('/plans');
                  }}
                >
                  Start Trial
                </button>
                <Link to="/login" className="login-link">
                  Login
                </Link>
              </div>
            ) : null}
          </div>


        </div>
      </div>

      {/* Login functionality moved to dedicated login page */}

      {/* Sign Up Modal - Moved to root level */}
      <SignUpModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
      />
    </header>
  );
};

export default Header;
